import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Loader, Select, Text } from 'src/@vymo/ui/atoms';
import { Accordion, Card } from 'src/@vymo/ui/blocks';
import { Keys, locale } from 'src/i18n';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  getAgentDocsGeneratingByDocId,
  getAgentDocsLinksByDocId,
  getAgentDocsLoadingState,
  getAgentDocsMessage,
  getAgentDocsResponseMessageByDocId,
  getAgentDocumentsState,
} from './selectors';
import { clearAgentDocsResponseMessage } from './slice';
import { generateAgentDocumentLinks } from './thunk';
import { AgentDocument, GeneratedLink } from './types';
import styles from './index.module.scss';

function AgentDocs() {
  const dispatch = useAppDispatch();
  const documents = useAppSelector(getAgentDocumentsState);
  const isLoadingDocuments = useAppSelector(getAgentDocsLoadingState);
  const generatingByDocId = useAppSelector(getAgentDocsGeneratingByDocId);
  const reportLinksByDocId = useAppSelector(getAgentDocsLinksByDocId);
  const responseMessageByDocId = useAppSelector(
    getAgentDocsResponseMessageByDocId,
  );
  const docsMessage = useAppSelector(getAgentDocsMessage);

  const [expandedDocId, setExpandedDocId] = useState<string>('');
  const [selectedFiltersByDocId, setSelectedFiltersByDocId] = useState<
    Record<string, Record<string, string>>
  >({});
  const [localResponseMessageByDocId, setLocalResponseMessageByDocId] =
    useState<Record<string, string>>({});

  useEffect(() => {
    if (!documents.length) {
      setExpandedDocId('');
      return;
    }

    setExpandedDocId((prevDocId) => {
      if (prevDocId && documents.some((doc) => doc.id === prevDocId)) {
        return prevDocId;
      }
      return '';
    });

    setSelectedFiltersByDocId((prevState) => {
      const updatedState = { ...prevState };
      documents.forEach((doc) => {
        if (updatedState[doc.id]) return;

        const defaultFilters = {};
        (doc?.filters || []).forEach((filter) => {
          defaultFilters[filter.code] = filter.options?.[0]?.value || '';
        });

        updatedState[doc.id] = defaultFilters;
      });
      return updatedState;
    });
  }, [documents]);

  const handleExpandDoc = useCallback((doc: AgentDocument, isOpen: boolean) => {
    setExpandedDocId(isOpen ? doc.id : '');

    setSelectedFiltersByDocId((prevState) => {
      if (prevState[doc.id]) return prevState;

      const defaultFilters = {};
      doc.filters.forEach((filter) => {
        defaultFilters[filter.code] = filter.options?.[0]?.value || '';
      });

      return {
        ...prevState,
        [doc.id]: defaultFilters,
      };
    });
  }, []);

  const handleGenerate = useCallback(
    async (doc: AgentDocument) => {
      const selectedFilters = selectedFiltersByDocId[doc.id] ?? {};
      const hasInvalidFilter = doc.filters.some(
        (filter) => !selectedFilters[filter.code],
      );

      if (hasInvalidFilter) {
        setLocalResponseMessageByDocId((prevState) => ({
          ...prevState,
          [doc.id]: locale(Keys.AGENT_DOCS_SELECT_ALL_FILTERS),
        }));
        return;
      }

      dispatch(clearAgentDocsResponseMessage({ docId: doc.id }));
      setLocalResponseMessageByDocId((prevState) => ({
        ...prevState,
        [doc.id]: '',
      }));

      dispatch(
        generateAgentDocumentLinks({
          docId: doc.id,
          filters: selectedFilters,
        }),
      );
    },
    [dispatch, selectedFiltersByDocId],
  );

  const renderResponseSection = useCallback(
    (responseMessage: string, reportLinks: GeneratedLink[]) => {
      if (!responseMessage && reportLinks.length === 0) {
        return null;
      }

      return (
        <div className={styles.manulifeDocs__item__linkCard}>
          {responseMessage ? (
            <Text classNames={styles.manulifeDocs__error}>
              {responseMessage}
            </Text>
          ) : (
            <>
              <Text semiBold classNames={styles.manulifeDocs__item__linkTitle}>
                Click the link to view the report
              </Text>
              {reportLinks.map((link) => (
                <Button
                  key={link.url}
                  type="link"
                  className={styles.manulifeDocs__item__linkButton}
                  onClick={() => window.location.assign(link.url)}
                >
                  {link.label}
                </Button>
              ))}
            </>
          )}
        </div>
      );
    },
    [],
  );

  const hasDocuments = useMemo(() => documents.length > 0, [documents.length]);

  return (
    <div className={styles.manulifeDocs}>
      <Text type="h5" bold underline classNames={styles.manulifeDocs__title}>
        {locale(Keys.AGENT_DOCS_TITLE)}
      </Text>

      <Loader
        visible={isLoadingDocuments}
        className={styles.manulifeDocs__loader}
      >
        {!hasDocuments && !isLoadingDocuments ? (
          <Card classNames={styles.manulifeDocs__responseCard}>
            <Text classNames={styles.manulifeDocs__error}>
              {docsMessage || locale(Keys.AGENT_DOCS_NO_DOCUMENTS)}
            </Text>
          </Card>
        ) : (
          <div className={styles.manulifeDocs__list}>
            {documents.map((doc) => {
              const selectedFilters = selectedFiltersByDocId[doc.id] ?? {};
              const isExpanded = expandedDocId === doc.id;
              const reportLinks: GeneratedLink[] =
                reportLinksByDocId[doc.id] ?? [];
              const isGeneratingThisDoc = Boolean(generatingByDocId[doc.id]);
              const responseMessage =
                localResponseMessageByDocId[doc.id] ||
                responseMessageByDocId[doc.id] ||
                '';

              return (
                <div key={doc.id} className={styles.manulifeDocs__item}>
                  <Accordion
                    title={
                      <Text classNames={styles.manulifeDocs__item__name}>
                        {doc.title}
                      </Text>
                    }
                    open={isExpanded}
                    onClick={(isOpen) => handleExpandDoc(doc, isOpen)}
                    border={false}
                    className={styles.manulifeDocs__item__accordion}
                  >
                    <Card classNames={styles.manulifeDocs__item__body}>
                      <div className={styles.manulifeDocs__item__filters}>
                        {doc.filters.map((filter) => (
                          <div
                            key={filter.code}
                            className={styles.manulifeDocs__item__filterItem}
                          >
                            <Text
                              type="subText"
                              classNames={
                                styles.manulifeDocs__item__filterLabel
                              }
                            >
                              {filter.label}
                            </Text>
                            <Select
                              options={filter.options}
                              value={
                                selectedFilters[filter.code]
                                  ? [selectedFilters[filter.code]]
                                  : []
                              }
                              placeholder={`Select ${filter.label}`}
                              onChange={(selectedOption) => {
                                setSelectedFiltersByDocId((prevState) => ({
                                  ...prevState,
                                  [doc.id]: {
                                    ...prevState[doc.id],
                                    [filter.code]:
                                      selectedOption?.[0]?.value || '',
                                  },
                                }));
                              }}
                            />
                          </div>
                        ))}
                      </div>

                      <div className={styles.manulifeDocs__item__actions}>
                        <Button
                          type="outlined"
                          className={styles.manulifeDocs__item__button}
                          loading={isGeneratingThisDoc}
                          onClick={() => handleGenerate(doc)}
                        >
                          Generate
                        </Button>
                      </div>

                      {doc.description && (
                        <Text
                          type="subText"
                          italic
                          classNames={styles.manulifeDocs__item__hint}
                        >
                          {doc.description}
                        </Text>
                      )}

                      {renderResponseSection(responseMessage, reportLinks)}
                    </Card>
                  </Accordion>
                </div>
              );
            })}
          </div>
        )}
      </Loader>
    </div>
  );
}

export default AgentDocs;
